import { ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import {
  type AbstractControl,
  type FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { debounceTime, distinctUntilChanged, merge, take } from 'rxjs';

import { emailValidator, noWhitespaceValidator } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { TellentMembersDataFacade, UserInvitationFacade } from '@core/tellent-logic';
import { AddEmployeeWithoutAccessPayload } from '@core/tellent-types';
import { mapEmptyStringsToNull } from '@core/tellent-util';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormErrorDirective,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

type FormField<T> = {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  email: FormControl<string | null>;
  personalEmail: FormControl<string | null>;
} & T;

type AddEmployeeForm = {
  employees: FormArray<FormGroup<FormField<{}>>>;
};

const removeDuplicateError = (control: AbstractControl<null | string, null | string> | null) => {
  if (control?.errors?.duplicatedEmail) {
    const { duplicatedEmail, ...errors } = control?.errors;
    control?.setErrors(errors && Object.keys(errors).length ? { ...errors } : null);
  }
};

@Component({
  selector: 'tla-add-employee-without-access-dialog',
  templateUrl: './add-employee-without-access-dialog.component.html',
  styleUrls: ['./add-employee-without-access-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ButtonComponent,
    TranslatePipe,
    FormFieldComponent,
    FormsModule,
    IconButtonComponent,
    InputComponent,
    LabelComponent,
    ProtectedControlDirective,
    ReactiveFormsModule,
    FormErrorDirective,
    IconComponent,
    ButtonLoadingDirective,
  ],
})
export class AddEmployeeWithoutAccessDialogComponent extends DialogComponent<{}> {
  private readonly t = injectTranslate();
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly userInvitationFacade = inject(UserInvitationFacade);

  private readonly fb = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);

  public loading = signal(false);
  public canAddRow = signal(true);

  public formGroup: FormGroup<AddEmployeeForm>;

  public emailFieldErrors = {
    required: this.t('tlc.common.errors.required'),
    email: this.t('tlc.common.errors.email'),
    duplicatedEmail: this.t('tlc.common.errors.duplicated_email'),
  };

  public ngOnInit(): void {
    this.formGroup = this.fb.group({
      employees: this.fb.array([this.createEmployeeGroup()]),
    });

    this.formGroup.controls.employees.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(300), this.untilDestroyed)
      .subscribe(employees => {
        const controls = this.formGroup.controls.employees.controls;
        const emails = controls.map(control => control.get('personalEmail')?.value);

        if (employees.length === 1) {
          const control = this.formGroup.controls.employees.controls[0].get('personalEmail');
          if (control) removeDuplicateError(control);
        } else {
          controls.forEach(control => {
            const email = control.get('personalEmail');
            const condition = emails.some((emailValue, i) =>
              emailValue ? emails.indexOf(emailValue) !== i && emailValue === email?.value : false,
            );

            if (condition) {
              email?.setErrors({ duplicatedEmail: true });
            } else {
              removeDuplicateError(email);
            }
          });
        }

        this.cdRef.detectChanges();
      });
  }

  public get employees() {
    return this.formGroup.controls.employees.controls;
  }

  public proceed(): void {
    const employees = <Partial<AddEmployeeWithoutAccessPayload>>(
      mapEmptyStringsToNull(this.formGroup.controls.employees.value)
    );
    this.loading.set(true);

    this.userInvitationFacade.addEmployeeWithoutAccess(employees);

    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  public cancel(): void {
    this.dialogRef.close(undefined);
  }

  public addMemberFormGroup() {
    this.formGroup.controls.employees.push(this.createEmployeeGroup());
    this.canAddRow.set(this.employees.length < 50);
    this.cdRef.detectChanges();
  }

  public removeMemberFormGroup(index: number) {
    this.formGroup.controls.employees.removeAt(index);
    this.canAddRow.set(this.formGroup.controls.employees.length < 50);
    this.cdRef.detectChanges();
  }

  private createEmployeeGroup() {
    return this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [noWhitespaceValidator, emailValidator]],
      personalEmail: ['', [noWhitespaceValidator, emailValidator]],
    });
  }

  private handleSubmitSuccess(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberSuccess$,
      this.membersDataFacade.inviteAppMemberSuccess$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(({ payload }) => {
        this.formGroup.markAsPristine();
        this.loading.set(false);

        this.dialogRef.close({ payload });
      });
  }

  private handleSubmitFailure(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberFail$,
      this.membersDataFacade.inviteAppMemberFail$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(() => this.loading.set(false));
  }
}
